/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { AppShell, Box, Burger, Group, Space, rem, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Suspense } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useSignOut } from '@fai/friendli-suite/features/auth/use-sign-out';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { useTeamRole } from '@fai/friendli-suite/features/settings/team-settings/use-role';
import { useTeams } from '@fai/friendli-suite/features/settings/team-settings/use-team';
import { useOpenCanny } from '@fai/friendli-suite/shared/canny/use-open-feedback';
import DocsWidget from '@fai/friendli-suite/shared/mintlify/docs-widget';
import DocumentButton from '@fai/friendli-suite/views/layout/header/basic/product/document-button';
import WebsiteButton from '@fai/friendli-suite/views/layout/header/basic/product/website-button';
import SuiteLogo from '@fai/friendli-suite/views/layout/header/items/suite-logo';
import { ProfileComboBox } from '@fai/friendli-suite/views/layout/header/profile-combobox';
import {
  ContainerHeaderMessage,
  DedicatedEndpointsHeaderMessage,
  ServerlessEndpointsHeaderMessage,
  GlobalHeaderMessage,
} from '@fai/friendli-suite/views/layout/header-messages';

import { HeaderContentContainer } from './header-contents';
import { NavbarContents } from './navbar-contents';
import { getProductLinksWithLabel } from './product-links-with-label';
import { ProductNavLinks } from './product-navlinks';
import { TeamComboBox } from './team-combobox';

function ProductBasicLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { teamId = '' } = useParams();
  const theme = useMantineTheme();

  // STATES
  const [navbarOpened, { toggle: toggleNavbar }] = useDisclosure();

  // ACTIONS
  const handleSignOut = useSignOut();
  const [handleFeedback] = useOpenCanny({
    postFieldCategory: 'feature-requests-feedback',
  });
  const handleOnClickUserSettings = () => navigate(Paths.userSettings.default);

  // TEAM SELECT SWITCH LIST
  const { teams } = useTeams();
  const { isTeamOwner } = useTeamRole(teamId);
  const selfFilteredTeams = teams?.filter((currentTeam) => currentTeam.id !== teamId) ?? [];

  const isUserInServerlessEndpointsPage = pathname.startsWith(
    Paths.team(teamId).serverlessEndpoints.default
  );
  const isUserInDedicatedEndpointsPage = pathname.startsWith(
    Paths.team(teamId).dedicatedEndpoints.default
  );
  const isUserInContainerPage = pathname.startsWith(Paths.team(teamId).container.default);

  return (
    <AppShell
      h="100%"
      header={{ height: rem(64) }}
      navbar={{
        width: 0,
        breakpoint: 'lg',
        collapsed: { mobile: !navbarOpened, desktop: true },
      }}
      styles={{
        header: {
          borderBottom: `${rem(1)} solid ${theme.pf.color.border.neutral.default}`,
        },
      }}
    >
      <AppShell.Header px={0}>
        <HeaderContentContainer
          leftItems={
            <>
              <Space w={theme.pf.layout.md} />
              <Burger
                hiddenFrom="lg"
                mr={theme.pf.layout.xs}
                opened={navbarOpened}
                onClick={toggleNavbar}
              />
              <SuiteLogo path={Paths.team(teamId).default} />

              <Box ml={theme.pf.layout['2xs']} visibleFrom="lg">
                <TeamComboBox isTeamOwner={isTeamOwner} teamId={teamId} teams={selfFilteredTeams} />
              </Box>
              <Group
                gap={theme.pf.layout.xl}
                h="100%"
                px={theme.pf.layout.md}
                visibleFrom="lg"
                wrap="nowrap"
              >
                <ProductNavLinks products={getProductLinksWithLabel(teamId)} />
              </Group>
            </>
          }
          rightItems={
            <>
              <Group gap={theme.pf.layout['2xs']} wrap="nowrap">
                <DocsWidget />
                <WebsiteButton />
                <DocumentButton />
              </Group>

              <Space w={theme.pf.layout.sm} />
              <ProfileComboBox
                handleOnClickUserSettings={handleOnClickUserSettings}
                handleSignOut={handleSignOut}
                handleFeedback={handleFeedback}
              />
              <Space w={theme.pf.layout.md} />
            </>
          }
        />
      </AppShell.Header>

      <AppShell.Navbar
        hiddenFrom="lg"
        maw={rem(375)}
        style={navbarOpened ? { boxShadow: theme.shadows.md } : undefined}
      >
        <NavbarContents isTeamOwner={isTeamOwner} teamId={teamId} teams={selfFilteredTeams} />
      </AppShell.Navbar>

      <AppShell.Main>
        {isUserInServerlessEndpointsPage ? (
          <ServerlessEndpointsHeaderMessage />
        ) : isUserInDedicatedEndpointsPage ? (
          <DedicatedEndpointsHeaderMessage />
        ) : isUserInContainerPage ? (
          <ContainerHeaderMessage />
        ) : (
          <GlobalHeaderMessage />
        )}
        <Suspense>
          <Outlet />
        </Suspense>
      </AppShell.Main>
    </AppShell>
  );
}

export default ProductBasicLayout;
